import { hyp } from '../core/exactMath';
import type { MapConfig } from '../config/gameConfig';
import { fbm, hash2 } from '../core/rng';
import type { GridPoint } from '../core/types';
import { TerrainType } from './terrain';
import { DecorationType, WorldMap, type MapLayout } from './WorldMap';
import type { MapShape } from './biome';

/**
 * Deterministic terrain generator. The same MapConfig always produces the
 * same map, which keeps bugs reproducible and tests stable.
 *
 * Guarantees (asserted by unit tests):
 *  - Every player slot sits inside an open, buildable clearing whose 3x3 core
 *    (the Command Center site) is plain grass.
 *  - Each infected spawn region sits in open, walkable ground.
 *  - A walkable highway connects every slot to every spawn region (the
 *    infected's natural approach — and a chokepoint to wall off).
 *  - The map edge ring is never water, rock or ruins (keeps the boundary readable).
 *  - (Phase 3) Every player slot gets a forest patch and a ruins patch just
 *    outside its clearing, inside building range, each with buildable ground
 *    around it, so a Sawmill and a Scrap Yard can always be built (maps of
 *    32+ tiles per side).
 */
export function generateMap(cfg: MapConfig): WorldMap {
  const { width, height, seed } = cfg;
  const layout = mapLayout(width, height);
  const map = new WorldMap(width, height, layout);

  // 1. Base terrain from three independent noise fields; the shape (D132) bends the thresholds and adds features.
  const scale = 0.09;
  const shape = cfg.shape ?? 'standard';
  const k = SHAPE_TUNING[shape];
  const n = Math.min(width, height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const moisture = fbm(x * scale, y * scale, seed + 11);
      const rockiness = fbm(x * scale * (shape === 'dunes' ? 2.2 : 1.4), y * scale * (shape === 'dunes' ? 2.2 : 1.4), seed + 37);
      const woods = fbm(x * scale * 1.2, y * scale * 1.2, seed + 73);

      let t: TerrainType = TerrainType.Grass;
      if (moisture < k.water) t = TerrainType.Water;
      else if (rockiness > k.rock) t = TerrainType.Rock;
      else if (woods > k.forest) t = TerrainType.Forest;
      else if (moisture > k.meadow) t = TerrainType.Meadow;
      else if (rockiness > k.rock - 0.08) t = TerrainType.Dirt;

      // Ruined settlements: clustered blocks of rubble on otherwise open land.
      if (t !== TerrainType.Water && t !== TerrainType.Rock) {
        const ruin = fbm(x * 0.16, y * 0.16, seed + 151);
        if (ruin > k.ruins) t = TerrainType.Ruins;
        else if (ruin > k.ruins - 0.06 && t !== TerrainType.Forest) t = TerrainType.Dirt;
      }

      // Shape features, measured across the map's anti-diagonal (colony slot 0 → centre → slot 1).
      if (shape !== 'standard') {
        // u: position along the colony→centre axis (0..1), v: signed distance from that axis in tiles.
        const along = (x - y + height) / (width + height);
        const v = (x + y - (n - 1)) / Math.SQRT2;
        const wob = (fbm(along * 6, 0.5, seed + 211) - 0.5) * n * 0.22;
        if (shape === 'river') {
          // A river across the axis, between the colony and the centre: the horde must cross it.
          const across = (x - y) / Math.SQRT2 + n * 0.22 + (fbm((x + y) * 0.05, 1.5, seed + 223) - 0.5) * n * 0.25;
          if (Math.abs(across) < 1.6 + fbm(x * 0.2, y * 0.2, seed + 227) * 1.4) t = TerrainType.Water;
        } else if (shape === 'canyon') {
          // Rock walls either side of a winding corridor that runs from the colony to the centre and on.
          const half = n * 0.2 + (fbm(along * 9, 2.5, seed + 229) - 0.5) * 6;
          if (Math.abs(v - wob) > half) t = TerrainType.Rock;
          else if (Math.abs(v - wob - half * 0.35) < 1.3) t = TerrainType.Water;
        } else if (shape === 'coast') {
          if ((x - y) / Math.SQRT2 > n * 0.32 + wob * 0.6) t = TerrainType.Water;
        }
      }

      // Keep the outer ring readable and passable.
      const edge = x === 0 || y === 0 || x === width - 1 || y === height - 1;
      if (edge && (t === TerrainType.Water || t === TerrainType.Rock || t === TerrainType.Ruins)) t = TerrainType.Grass;

      map.setTerrain(x, y, t);
    }
  }

  // 2. Highways from every slot to each spawn region (carved before clearings).
  for (const slot of layout.playerSlots) for (const r of layout.spawnRegions) carveRoad(map, slot, r.center, seed);

  // 3. Open clearings: every colony site and each spawn region.
  for (const slot of layout.playerSlots) clearAround(map, slot, cfg.startClearRadius);
  for (const r of layout.spawnRegions) clearAround(map, r.center, Math.max(2, r.radius));

  // 3b. Starter resource sites near every colony.
  for (const slot of layout.playerSlots) ensureResourceSites(map, slot, cfg.startClearRadius, seed);

  // 4. Cosmetic variants and decorations.
  decorate(map, seed);

  return map;
}

/**
 * Give every colony wood and scrap to exploit: a small forest patch and a
 * small ruins patch on the ring just outside the clearing, each surrounded by
 * a buildable apron (so a Sawmill / Scrap Yard always has a site next to it).
 * Patches never touch roads, clearings, spawn regions, the map edge or each
 * other. Deterministic (candidate choice is hashed).
 */
function ensureResourceSites(map: WorldMap, slot: GridPoint, clearRadius: number, seed: number): void {
  const ring = clearRadius + 3;
  const placed: GridPoint[] = [];
  const wants: { t: TerrainType; radius: number }[] = [
    { t: TerrainType.Forest, radius: 1.6 },
    { t: TerrainType.Ruins, radius: 1.25 },
  ];
  for (const want of wants) {
    let best: GridPoint | null = null;
    let bestScore = Infinity;
    for (let dy = -ring; dy <= ring; dy++) {
      for (let dx = -ring; dx <= ring; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== ring) continue;
        const c = { x: slot.x + dx, y: slot.y + dy };
        if (!patchFits(map, c, want.radius, clearRadius) || placed.some((p) => hyp(p.x - c.x, p.y - c.y) < 5)) continue;
        const score = hash2(c.x, c.y, seed + 401 + want.t);
        if (score < bestScore) (bestScore = score), (best = c);
      }
    }
    if (!best) continue; // Map too small / crowded: no guarantee (documented: 32+ tiles).
    placed.push(best);
    stampPatch(map, best, want.radius, want.t);
    // Buildable apron around the patch (never over roads or other patches).
    for (const p of patchTiles(best, want.radius + 2.2)) {
      if (hyp(p.x - best.x, p.y - best.y) <= want.radius) continue;
      if (!map.inBounds(p.x, p.y) || map.getTerrain(p.x, p.y) === TerrainType.Road) continue;
      if (placed.some((q) => q !== best && hyp(p.x - q.x, p.y - q.y) <= 1.7)) continue;
      if (!map.isBuildable(p.x, p.y)) map.setTerrain(p.x, p.y, TerrainType.Grass);
    }
  }
}

function patchTiles(c: GridPoint, radius: number): GridPoint[] {
  const out: GridPoint[] = [];
  const r = Math.ceil(radius);
  for (let y = c.y - r; y <= c.y + r; y++) for (let x = c.x - r; x <= c.x + r; x++) if (hyp(x - c.x, y - c.y) <= radius) out.push({ x, y });
  return out;
}

function patchFits(map: WorldMap, c: GridPoint, radius: number, clearRadius: number): boolean {
  for (const t of patchTiles(c, radius + 1)) {
    if (t.x < 1 || t.y < 1 || t.x > map.width - 2 || t.y > map.height - 2) return false;
    if (map.getTerrain(t.x, t.y) === TerrainType.Road) return false;
    if (map.spawnRegionAt(t.x, t.y) !== null) return false;
  }
  for (const t of patchTiles(c, radius)) {
    if (map.playerSlots.some((s) => hyp(t.x - s.x, t.y - s.y) <= clearRadius + 0.5)) return false;
    if (map.getTerrain(t.x, t.y) === TerrainType.Water) return false; // never a patch in a lake
  }
  return true;
}

function stampPatch(map: WorldMap, c: GridPoint, radius: number, t: TerrainType): void {
  for (const p of patchTiles(c, radius)) map.setTerrain(p.x, p.y, t);
}

/** Threshold set per map shape (D132). `standard` keeps the original maps byte-identical. */
const SHAPE_TUNING: Record<MapShape, { water: number; rock: number; forest: number; meadow: number; ruins: number }> = {
  standard: { water: 0.3, rock: 0.68, forest: 0.6, meadow: 0.62, ruins: 0.72 },
  river: { water: 0.24, rock: 0.7, forest: 0.6, meadow: 0.6, ruins: 0.74 },
  canyon: { water: 0.2, rock: 0.74, forest: 0.64, meadow: 0.64, ruins: 0.74 },
  lakes: { water: 0.4, rock: 0.72, forest: 0.58, meadow: 0.6, ruins: 0.74 },
  coast: { water: 0.26, rock: 0.7, forest: 0.6, meadow: 0.62, ruins: 0.73 },
  dunes: { water: 0.14, rock: 0.62, forest: 0.74, meadow: 0.7, ruins: 0.74 },
  forest: { water: 0.26, rock: 0.72, forest: 0.47, meadow: 0.64, ruins: 0.75 },
  city: { water: 0.24, rock: 0.74, forest: 0.68, meadow: 0.66, ruins: 0.6 },
};

/** Maximum player slots any map provides (master plan: 2–4 players). */
export const MAX_PLAYER_SLOTS = 4;

/**
 * Four player slots, one per corner region (in iso view: left, right, top,
 * bottom), and one infected spawn region in the centre, equidistant from all
 * slots. Slot order: 0 = west (bottom-left of the grid), 1 = east (opposite),
 * 2 = north, 3 = south, so a 2-player match always gets opposite corners.
 *
 * The spawn radius is limited so the zone never reaches any 3x3 Command
 * Center site. On maps too small to fit a zone with that clearance
 * (below ~14 tiles), no spawn region is created.
 */
export function mapLayout(width: number, height: number): MapLayout {
  const insetX = Math.max(2, Math.round(width * 0.18));
  const insetY = Math.max(2, Math.round(height * 0.18));
  const left = insetX;
  const right = width - 1 - insetX;
  const top = insetY;
  const bottom = height - 1 - insetY;
  const playerSlots = [
    { x: left, y: bottom },
    { x: right, y: top },
    { x: left, y: top },
    { x: right, y: bottom },
  ];
  const center = { x: Math.floor((width - 1) / 2), y: Math.floor((height - 1) / 2) };
  // Nearest Command Center tile is at most √2 tiles closer than its centre; keep a further 1-tile gap.
  const nearest = Math.min(...playerSlots.map((s) => hyp(center.x - s.x, center.y - s.y)));
  const clearance = Math.floor(nearest - Math.SQRT2 - 1);
  const radius = Math.min(4, Math.max(1, Math.floor(Math.min(width, height) / 12)), clearance);
  return { playerSlots, spawnRegions: radius >= 1 ? [{ id: 'center', center, radius }] : [] };
}

function clearAround(map: WorldMap, c: GridPoint, r: number): void {
  for (let y = c.y - r; y <= c.y + r; y++) {
    for (let x = c.x - r; x <= c.x + r; x++) {
      if (!map.inBounds(x, y)) continue;
      const d = hyp(x - c.x, y - c.y);
      if (d > r + 0.35) continue;
      const t = map.getTerrain(x, y);
      // The 3x3 core (Command Center site / spawn point) is always open grass, even under the road's first steps.
      if (Math.abs(x - c.x) <= 1 && Math.abs(y - c.y) <= 1) {
        map.setTerrain(x, y, TerrainType.Grass);
        continue;
      }
      if (t === TerrainType.Road) continue;
      map.setTerrain(x, y, d > r - 1.2 && t === TerrainType.Meadow ? TerrainType.Meadow : TerrainType.Grass);
    }
  }
}

/**
 * A gently wandering road: walk from a to b one step at a time, preferring the
 * axis with the larger remaining distance, with noise-driven deviation.
 * Each step moves along exactly one axis, so the road is 4-connected.
 */
function carveRoad(map: WorldMap, a: GridPoint, b: GridPoint, seed: number): void {
  let x = a.x;
  let y = a.y;
  let step = 0;
  const pave = (px: number, py: number): void => {
    if (map.inBounds(px, py)) map.setTerrain(px, py, TerrainType.Road);
  };
  pave(x, y);
  while (x !== b.x || y !== b.y) {
    const dx = b.x - x;
    const dy = b.y - y;
    const wobble = hash2(step, 0, seed + 501);
    const moveX = dy === 0 || (dx !== 0 && (Math.abs(dx) / (Math.abs(dx) + Math.abs(dy)) > wobble));
    if (moveX) x += Math.sign(dx);
    else y += Math.sign(dy);
    pave(x, y);
    step++;
  }
}

function decorate(map: WorldMap, seed: number): void {
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      const i = map.index(x, y);
      const r = hash2(x, y, seed + 900);
      map.variant[i] = Math.floor(hash2(x, y, seed + 901) * 4);
      let d: DecorationType = DecorationType.None;
      const onColonySite = map.playerSlots.some((s) => Math.abs(x - s.x) <= 1 && Math.abs(y - s.y) <= 1);
      const inSpawn = map.spawnRegionAt(x, y) !== null;
      switch (onColonySite ? -1 : map.terrain[i]) {
        case TerrainType.Forest:
          d = r < 0.45 ? DecorationType.Pine : r < 0.78 ? DecorationType.Oak : r < 0.93 ? DecorationType.DeadTree : DecorationType.Bush;
          break;
        case TerrainType.Ruins:
          d = DecorationType.Rubble;
          break;
        case TerrainType.Dirt:
          d = r < 0.05 ? DecorationType.DeadTree : DecorationType.None;
          break;
        case TerrainType.Rock:
          d = DecorationType.Boulder;
          break;
        case TerrainType.Meadow:
          d = r < 0.18 ? DecorationType.Flowers : r < 0.24 ? DecorationType.Bush : DecorationType.None;
          break;
        case TerrainType.Grass:
          d = r < 0.035 ? DecorationType.Bush : r < 0.06 ? DecorationType.Flowers : r < 0.075 ? DecorationType.DeadTree : DecorationType.None;
          break;
        case TerrainType.Water:
          d = hasLandNeighbour(map, x, y) && r < 0.3 ? DecorationType.Reeds : DecorationType.None;
          break;
        default:
          break;
      }
      // Infestation marks the spawn region on open ground (cosmetic only).
      if (inSpawn && d === DecorationType.None && map.isWalkable(x, y) && r < 0.45) d = DecorationType.Infestation;
      map.decoration[i] = d;
    }
  }
}

function hasLandNeighbour(map: WorldMap, x: number, y: number): boolean {
  for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
    const nx = x + dx;
    const ny = y + dy;
    if (map.inBounds(nx, ny) && map.getTerrain(nx, ny) !== TerrainType.Water) return true;
  }
  return false;
}
